import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { Clock, PencilLine } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" }) : "—");
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function Timekeeping() {
  const { user } = useAuth();
  const [entries, setEntries] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [editing, setEditing] = useState(null); // { entry, field, value, reason }
  const [pay, setPay] = useState(null);
  const [range, setRange] = useState("day");
  const [report, setReport] = useState(null);
  const [showAllEntries, setShowAllEntries] = useState(false);

  const load = async () => {
    const [e, c, p] = await Promise.all([
      api.get("/time/entries"), api.get("/time/corrections"), api.get("/time/my-pay"),
    ]);
    setEntries(e.data); setCorrections(c.data); setPay(p.data);
  };
  const loadReport = async (r) => {
    const { data } = await api.get("/time/pay-report", { params: { range: r } });
    setReport(data.employees.find((x) => x.user_id === user.id) || null);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadReport(range); }, [range]);
  if (!entries) return <Loading label="Loading timesheet" />;

  const pendingFor = (entryId) => corrections.find((c) => c.entry_id === entryId && ["pending_manager", "pending_owner"].includes(c.status));

  const openEdit = (entry) => setEditing({ entry, field: entry.clock_out ? "clock_out" : "clock_in", value: toLocalInput(entry.clock_in), reason: "" });

  const submit = async () => {
    if (!editing.reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/time/entries/${editing.entry.id}/correction-request`, {
        field: editing.field, requested_value: new Date(editing.value).toISOString(), reason: editing.reason,
      });
      toast.success("Correction requested — awaiting Manager review, then Owner approval");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
  };

  const openEntry = entries.find((e) => !e.clock_out);
  const lastClosed = entries.find((e) => e.clock_out);

  return (
    <div className="max-w-4xl">
      <SectionTitle sub="Your clock records · request a correction if a time is wrong (Manager reviews, Owner approves). Original times are never overwritten — every correction is kept as history.">My Time & Pay</SectionTitle>

      <div className="sg-card p-5 mb-4" data-testid="current-clock-status">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-head text-lg font-bold">Current Clock Status</div>
          {openEntry ? <StatusChip status="ready" label="CLOCKED IN" /> : <StatusChip status="unpaid" label="CLOCKED OUT" />}
        </div>
        {openEntry ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock In</div><div className="font-mono text-xs mt-0.5">{fmtTime(openEntry.clock_in)}</div></div>
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock-In Location</div><div className="text-xs mt-0.5" style={{ color: "var(--txt2)" }} data-testid="current-ci-loc">{openEntry.clock_in_location || "LOCATION UNAVAILABLE"}</div></div>
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Active Duration</div><div className="mt-0.5" data-testid="active-duration">{fmtDur(Math.floor((Date.now() - new Date(openEntry.clock_in).getTime()) / 1000))}</div></div>
          </div>
        ) : lastClosed ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Last Clock Out</div><div className="font-mono text-xs mt-0.5">{fmtTime(lastClosed.clock_out)}</div></div>
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Last Clock-Out Location</div><div className="text-xs mt-0.5" style={{ color: "var(--txt2)" }} data-testid="current-co-loc">{lastClosed.clock_out_location || "LOCATION UNAVAILABLE"}</div></div>
          </div>
        ) : <div className="text-sm" style={{ color: "var(--txt2)" }}>No clock records yet — clock in from Home to start.</div>}
      </div>

      {pay && (
        <div className="sg-card p-5 mb-4" data-testid="my-time-pay">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="font-head text-lg font-bold">Estimated Pay</div>
            {pay.clocked_in ? <StatusChip status="ready" label="On clock" /> : <StatusChip status="unpaid" label="Off clock" />}
          </div>
          <div className="flex gap-1 mb-4" data-testid="mypay-range-tabs">
            {["day", "week", "month", "year"].map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`mypay-range-${r}`}
                className="sg-btn" style={{ minHeight: 34, padding: "4px 14px", textTransform: "uppercase", fontSize: 12,
                  background: range === r ? "var(--accent)" : undefined, color: range === r ? "#fff" : undefined,
                  borderColor: range === r ? "var(--accent)" : undefined }}>{r}</button>
            ))}
          </div>
          {report ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <PayCell k={`Total Hours (${range})`} v={`${report.total_hours}h`} testid="pay-total-hours" />
                <PayCell k={`Hourly Wages (× $${report.hourly_rate})`} v={`$${report.hourly_pay}`} testid="pay-hourly" />
                <PayCell k="Production Revenue Credited" v={`$${report.production_revenue ?? 0}`} testid="pay-prod-rev" />
                <PayCell k="Production Bonus" v={`$${report.production_bonus}`} testid="pay-prod-bonus" />
                <PayCell k="Membership Revenue Credited" v={`$${report.membership_revenue ?? 0}`} testid="pay-mem-rev" />
                <PayCell k="Membership Bonus" v={`$${report.membership_bonus}`} testid="pay-mem-bonus" />
                <div className="col-span-2 sm:col-span-3 p-3 rounded-lg" style={{ background: "rgba(255,87,34,0.1)" }}>
                  <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Estimated Gross Pay ({range})</div>
                  <div className="font-head text-2xl font-extrabold" style={{ color: "var(--accent)" }} data-testid="pay-est-gross">${report.estimated_gross}</div>
                </div>
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--txt2)" }} data-testid="pay-disclaimer">{pay.disclaimer}</div>
            </>
          ) : <div className="text-sm" style={{ color: "var(--txt2)" }}>No pay data for this range.</div>}
        </div>
      )}

      <div className="flex flex-col gap-3" data-testid="timesheet-list">
        <div className="font-head text-lg font-bold mt-2" data-testid="clock-log-heading">Clock In / Out Log</div>
        {entries.length === 0 && <div className="sg-card p-6 text-sm" style={{ color: "var(--txt2)" }}>No time entries yet. Clock in from Home to start.</div>}
        {entries.length > 5 && (
          <button className="text-xs font-semibold self-start" style={{ color: "var(--accent)" }} onClick={() => setShowAllEntries((v) => !v)} data-testid="clock-history-toggle">
            {showAllEntries ? "Collapse" : `View all (${entries.length})`}
          </button>
        )}
        <div className={showAllEntries ? "flex flex-col gap-3" : "flex flex-col gap-3"} style={showAllEntries ? { maxHeight: 420, overflowY: "auto" } : undefined} data-testid="clock-history-list">
        {(showAllEntries ? entries : entries.slice(0, 5)).map((e) => {
          const pending = pendingFor(e.id);
          return (
            <div key={e.id} className="sg-card p-4" data-testid={`timesheet-entry-${e.id}`}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--accent)" }} data-testid={`entry-date-${e.id}`}>{fmtDate(e.clock_in)}</div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Clock size={18} style={{ color: "var(--accent)" }} />
                  <span className="font-semibold">{e.user_name}</span>
                  {!e.clock_out ? <StatusChip status="ready" label="On clock" /> : <StatusChip status="unpaid" label="Closed" />}
                  {e.hub && <span className="text-xs" style={{ color: "var(--txt2)" }}>· {e.hub}</span>}
                </div>
                {e.clock_out && !pending && (
                  <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => openEdit(e)} data-testid={`request-correction-${e.id}`}>
                    <PencilLine size={14} /> Request correction
                  </button>
                )}
                {pending && <StatusChip status="warning" label={pending.status === "pending_owner" ? "Correction: awaiting Owner" : "Correction: awaiting Manager"} />}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 text-sm">
                <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock In</div><div className="font-mono text-xs mt-0.5">{fmtTime(e.clock_in)}</div><div className="text-[11px] mt-0.5" style={{ color: "var(--txt2)" }} data-testid={`ci-loc-${e.id}`}>{e.clock_in_location || "LOCATION UNAVAILABLE"}</div></div>
                <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Clock Out</div><div className="font-mono text-xs mt-0.5">{fmtTime(e.clock_out)}</div><div className="text-[11px] mt-0.5" style={{ color: "var(--txt2)" }} data-testid={`co-loc-${e.id}`}>{e.clock_out ? (e.clock_out_location || "LOCATION UNAVAILABLE") : "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Duration</div><div className="mt-0.5">{fmtDur(e.duration_seconds)}</div><div className="text-[11px] mt-0.5" style={{ color: e.clock_out ? "var(--txt2)" : "#10B981" }} data-testid={`status-${e.id}`}>{e.clock_out ? "Complete" : "On clock"}</div></div>
              </div>
              {(e.adjustments || []).length > 0 && (
                <details className="mt-3" data-testid={`timesheet-history-${e.id}`}>
                  <summary className="text-xs cursor-pointer" style={{ color: "var(--accent)" }}>{e.adjustments.length} correction(s) — view audit history</summary>
                  <div className="mt-2 flex flex-col gap-2">
                    {e.adjustments.map((a, i) => (
                      <div key={i} className="text-xs p-2 rounded-lg" style={{ background: "var(--bg-elev)", color: "var(--txt2)" }}>
                        <b style={{ color: "var(--txt)" }}>{a.field}</b>: <span className="font-mono">{fmtTime(a.from)}</span> → <span className="font-mono" style={{ color: "var(--accent)" }}>{fmtTime(a.to)}</span>
                        <div>Requested by {a.requested_by || "—"}{a.manager_name ? ` · Manager ${a.manager_name} recommended ${a.manager_recommendation} (${a.manager_reason})` : ""}</div>
                        <div>{a.direct_owner_correction ? "Direct owner correction" : "Owner"} {a.decided_by} — {a.owner_reason} · {fmtTime(a.at)}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
      </div>


      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="correction-modal">
          <div className="sg-card p-5 w-full max-w-md">
            <div className="font-head font-bold mb-3">Request Time Correction</div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Which time?</label>
            <select className="sg-input mt-1 mb-3" value={editing.field} onChange={(ev) => setEditing((s) => ({ ...s, field: ev.target.value, value: toLocalInput(ev.target.value === "clock_out" ? s.entry.clock_out : s.entry.clock_in) }))} data-testid="correction-field">
              <option value="clock_in">Clock In</option>
              <option value="clock_out">Clock Out</option>
            </select>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Correct time</label>
            <input type="datetime-local" className="sg-input mt-1 mb-3" value={editing.value} onChange={(ev) => setEditing((s) => ({ ...s, value: ev.target.value }))} data-testid="correction-value" />
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Reason (required)</label>
            <textarea className="sg-input mt-1 mb-4" rows={2} value={editing.reason} onChange={(ev) => setEditing((s) => ({ ...s, reason: ev.target.value }))} data-testid="correction-reason" placeholder="e.g. Forgot to clock in when we arrived" />
            <div className="flex gap-2">
              <button className="sg-btn sg-btn-accent flex-1" onClick={submit} data-testid="correction-submit">Submit Request</button>
              <button className="sg-btn" onClick={() => setEditing(null)} data-testid="correction-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayCell({ k, v, testid }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--bg-elev)" }}>
      <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--txt2)" }}>{k}</div>
      <div className="font-head text-lg font-bold mt-0.5" data-testid={testid}>{v}</div>
    </div>
  );
}
